import { POKE_DATA } from "game/data/pokemon/data";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import { PokemonType } from "types/battle-engine/battle";
import type { PokeNames } from "types/pokemon/poke-names";
import { getPokeSkills, SKILL_TYPE_LABEL } from "@/components/cards/utils/pokemon-card-data";
import { getTypeEffectiveness } from "@/lib/battle/utils/type-effectiveness";
import { calculatePurityScore, getPurityInfo } from "@/utils/purity";
import type { PurityRank } from "@/utils/purity";

export type Rarity = "common" | "uncommon" | "rare" | "ultra" | "legendary";

const RARITY_LABEL: Record<Rarity, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  ultra: "Ultra Rare",
  legendary: "Secret Rare",
};

const RARITY_DOTS: Record<Rarity, number> = {
  common: 1,
  uncommon: 2,
  rare: 3,
  ultra: 4,
  legendary: 5,
};

export interface TcgMove {
  id: string;
  name: string;
  power: number;
  type: PokemonType;
  /** Short effect kind, e.g. "DMG" / "HEAL" / "SHIELD". */
  kind: string;
  cost: number;
}

export interface TcgCardData {
  types: PokemonType[];
  /** "FIRE · FLYING" style descriptor line. */
  typeLine: string;
  /** TCG-style HP, derived from the resistance stat. */
  hp: number;
  rarity: Rarity;
  rarityLabel: string;
  rarityDots: number;
  dexNumber: number;
  /** Formatted dex id, e.g. "#025". */
  dexId: string;
  heightM: number;
  weightKg: number;
  stats: { ap: number; res: number; spd: number; total: number };
  /** Type this mon takes the most damage from (weakness footer). */
  weakness: PokemonType | null;
  /** Type this mon best resists (resistance footer). */
  resistanceType: PokemonType | null;
  /** Rough retreat cost, scaled from weight. 0..4. */
  retreat: number;
  /** Evolution stage (1-based) and total stages in the chain. */
  evoStage: number;
  evoTotal: number;
  /** Short "Stage 1/2" or "Basic" label. */
  stageLabel: string;
  isShiny: boolean;
  isLegendary: boolean;
  isMega: boolean;
  /** Talent affinity types the owned instance rolled (0-9). */
  talents: PokemonType[];
  /** How many talents match the species' own types (0-9). */
  purity: number;
  /** Talent rank D → S+ derived from purity. */
  talentRank: PurityRank;
  talentColor: string;
  talentGlow: string;
  /** Normalized moves (typed, with power/kind) for attack lists. */
  attacks: TcgMove[];
  moves: ReturnType<typeof getPokeSkills>;
}

const toPokemonType = (raw: string | undefined): PokemonType =>
  (raw ?? "normal").toUpperCase() as PokemonType;

/** Cheapest, most-generic incoming type each type struggles against. */
const worstMatchup = (defenderTypes: PokemonType[]) => {
  let weakness: PokemonType | null = null;
  let weakScore = 1;
  let resistanceType: PokemonType | null = null;
  let resistScore = 1;

  for (const attacker of Object.values(PokemonType)) {
    // Combined multiplier the defender takes from this attacking type.
    const mult = defenderTypes.reduce(
      (acc, def) => acc * getTypeEffectiveness(attacker, def),
      1,
    );
    if (mult > weakScore) {
      weakScore = mult;
      weakness = attacker;
    }
    if (mult < resistScore) {
      resistScore = mult;
      resistanceType = attacker;
    }
  }
  return { weakness, resistanceType };
};

const deriveRarity = (
  data: (typeof POKE_DATA)[PokeNames],
  statTotal: number,
): Rarity => {
  if (data.is_legendary || data.is_mythical) return "legendary";
  if (data.is_mega || data.is_gmax) return "ultra";
  if (statTotal >= 320) return "rare";
  if (statTotal >= 240) return "uncommon";
  return "common";
};

/** Position of this species in its evolution chain (1-based) + chain length. */
const deriveEvoStage = (data: (typeof POKE_DATA)[PokeNames]) => {
  const chain = data.evolution_chain?.species ?? [];
  const total = Math.max(1, chain.length);
  const idx = chain.findIndex((s) => s.name === data.name);
  return { stage: idx >= 0 ? idx + 1 : 1, total };
};

const stageLabelFor = (stage: number, total: number): string => {
  if (total <= 1) return "Basic";
  if (stage <= 1) return "Basic";
  return `Stage ${stage - 1}`;
};

/** Derive a full TCG-ready view model for a species + owned instance. */
export const getTcgCardData = (
  pokeName: string,
  opts: {
    isShiny?: boolean | undefined;
    activeSkills?: readonly string[] | undefined;
    talents?: readonly string[] | undefined;
  } = {},
): TcgCardData => {
  const data = POKE_DATA[pokeName as PokeNames];
  const types = (data?.types ?? ["normal"]).map(toPokemonType);
  const ap = Math.round(data?.stats.ability_power ?? 50);
  const res = Math.round(data?.stats.resistance ?? 50);
  const spd = Math.round(data?.stats.speed ?? 50);
  const total = ap + res + spd;
  const { weakness, resistanceType } = worstMatchup(types);
  const rarity = data ? deriveRarity(data, total) : "common";
  const { stage, total: evoTotal } = data
    ? deriveEvoStage(data)
    : { stage: 1, total: 1 };

  // Prefer the owned instance's equipped skills; fall back to species moves.
  const rawMoves =
    opts.activeSkills && opts.activeSkills.length > 0
      ? opts.activeSkills
          .map((id) => POKEMON_SKILLS[id])
          .filter((s): s is NonNullable<typeof s> => Boolean(s))
      : getPokeSkills(pokeName);
  const attacks: TcgMove[] = rawMoves.map((m) => ({
    id: m.id,
    name: m.name,
    power: m.power,
    type: (m.pokemon_type ?? types[0] ?? PokemonType.NORMAL) as PokemonType,
    kind: SKILL_TYPE_LABEL[m.types[0] ?? ""] ?? m.types[0] ?? "",
    cost: Math.max(1, m.mana_cost ?? 1),
  }));

  const talents = (opts.talents ?? []).map(toPokemonType);
  const purity = calculatePurityScore(
    talents.map(String),
    types.map(String),
  );
  const purityInfo = getPurityInfo(purity);

  return {
    types,
    typeLine: types.join(" · "),
    hp: Math.round((res * 2 + 20) / 10) * 10,
    rarity,
    rarityLabel: RARITY_LABEL[rarity],
    rarityDots: RARITY_DOTS[rarity],
    dexNumber: data?.id ?? 0,
    dexId: `#${String(data?.id ?? 0).padStart(3, "0")}`,
    heightM: (data?.height ?? 0) / 10,
    weightKg: (data?.weight ?? 0) / 10,
    stats: { ap, res, spd, total },
    weakness,
    resistanceType,
    retreat: Math.max(0, Math.min(4, Math.round((data?.weight ?? 0) / 900))),
    evoStage: stage,
    evoTotal,
    stageLabel: stageLabelFor(stage, evoTotal),
    isShiny: Boolean(opts.isShiny),
    isLegendary: Boolean(data?.is_legendary || data?.is_mythical),
    isMega: Boolean(data?.is_mega || data?.is_gmax),
    talents,
    purity,
    talentRank: purityInfo.rank,
    talentColor: purityInfo.glowColor === "transparent" ? "#9aa0ad" : purityInfo.glowColor,
    talentGlow: purityInfo.glowColor,
    attacks,
    moves: rawMoves,
  };
};
